import { interpolateHexColours, mixHexColour } from './colour';

export const CATEGORICAL_PALETTES = [
  {
    id: 'balanced',
    name: 'Balanced',
    colours: [
      '#4477aa',
      '#ee6677',
      '#228833',
      '#ccbb44',
      '#66ccee',
      '#aa3377',
      '#bbbbbb',
      '#ee8866',
    ],
  },
  {
    id: 'coastal',
    name: 'Coastal',
    colours: [
      '#176f68',
      '#3aa99f',
      '#78c6b8',
      '#d6b85a',
      '#d98252',
      '#a84855',
      '#6d597a',
      '#355070',
    ],
  },
  {
    id: 'soft',
    name: 'Soft',
    colours: [
      '#4e79a7',
      '#f28e2b',
      '#e15759',
      '#76b7b2',
      '#59a14f',
      '#edc948',
      '#b07aa1',
      '#ff9da7',
    ],
  },
] as const;

export const SEQUENTIAL_PALETTES = [
  {
    id: 'blues',
    name: 'Blue',
    kind: 'sequential',
    colours: ['#eff6ff', '#bfdbfe', '#60a5fa', '#2563eb', '#1e3a8a'],
  },
  {
    id: 'teal',
    name: 'Teal',
    kind: 'sequential',
    colours: ['#ecfdf5', '#a7f3d0', '#34d399', '#0f8a74', '#064e3b'],
  },
  {
    id: 'ember',
    name: 'Orange',
    kind: 'sequential',
    colours: ['#fff7ed', '#fed7aa', '#fb923c', '#dc4c2e', '#7c2d12'],
  },
  {
    id: 'purple',
    name: 'Purple',
    kind: 'sequential',
    colours: ['#fcfbfd', '#dadaeb', '#9e9ac8', '#6a51a3', '#3f007d'],
  },
  {
    id: 'pink',
    name: 'Pink',
    kind: 'sequential',
    colours: ['#fff7f3', '#fcc5c0', '#f768a1', '#ae017e', '#49006a'],
  },
  {
    id: 'white-red',
    name: 'Red',
    kind: 'sequential',
    colours: ['#fff5f0', '#fcbba1', '#fb6a4a', '#cb181d', '#67000d'],
  },
  {
    id: 'white-green',
    name: 'Green',
    kind: 'sequential',
    colours: ['#f7fcf5', '#c7e9c0', '#74c476', '#238b45', '#00441b'],
  },
  {
    id: 'red-green',
    name: 'Red ↔ Green',
    kind: 'diverging',
    colours: ['#a50026', '#f46d43', '#f7f7f7', '#66bd63', '#006837'],
  },
  {
    id: 'blue-red',
    name: 'Blue ↔ Red',
    kind: 'diverging',
    colours: ['#2166ac', '#67a9cf', '#f7f7f7', '#ef8a62', '#b2182b'],
  },
  {
    id: 'blue-orange',
    name: 'Blue ↔ Orange',
    kind: 'diverging',
    colours: ['#2166ac', '#67a9cf', '#f7f7f7', '#fdae61', '#b35806'],
  },
  {
    id: 'purple-green',
    name: 'Purple ↔ Green',
    kind: 'diverging',
    colours: ['#762a83', '#af8dc3', '#f7f7f7', '#7fbf7b', '#1b7837'],
  },
] as const;

export const GRADUATED_PALETTE_GROUPS = [
  {
    kind: 'sequential',
    name: 'Sequential',
    palettes: SEQUENTIAL_PALETTES.filter((palette) => palette.kind === 'sequential'),
  },
  {
    kind: 'diverging',
    name: 'Diverging',
    palettes: SEQUENTIAL_PALETTES.filter((palette) => palette.kind === 'diverging'),
  },
] as const;

export type CategoricalPaletteId = (typeof CATEGORICAL_PALETTES)[number]['id'];
export type SequentialPaletteId = (typeof SEQUENTIAL_PALETTES)[number]['id'];
export type PaletteId = CategoricalPaletteId | SequentialPaletteId;

export const DEFAULT_CATEGORICAL_PALETTE_ID: CategoricalPaletteId = 'balanced';
export const DEFAULT_SEQUENTIAL_PALETTE_ID: SequentialPaletteId = 'blues';

export function isCategoricalPaletteId(value: unknown): value is CategoricalPaletteId {
  return CATEGORICAL_PALETTES.some((palette) => palette.id === value);
}

export function isSequentialPaletteId(value: unknown): value is SequentialPaletteId {
  return SEQUENTIAL_PALETTES.some((palette) => palette.id === value);
}

export function categoricalPaletteColours(
  paletteId: CategoricalPaletteId,
  count: number,
): readonly string[] {
  const palette =
    CATEGORICAL_PALETTES.find((candidate) => candidate.id === paletteId) ?? CATEGORICAL_PALETTES[0];
  return Array.from({ length: count }, (_, index) => {
    const base = palette.colours[index % palette.colours.length] ?? '#64748b';
    const cycle = Math.floor(index / palette.colours.length);
    if (cycle === 1) return mixHexColour(base, '#ffffff', 0.24);
    if (cycle >= 2) return mixHexColour(base, '#000000', 0.2);
    return base;
  });
}

export function sequentialPaletteColours(
  paletteId: SequentialPaletteId,
  count: number,
  reversed = false,
): readonly string[] {
  const palette =
    SEQUENTIAL_PALETTES.find((candidate) => candidate.id === paletteId) ?? SEQUENTIAL_PALETTES[0];
  if (count <= 0) return [];
  if (count === 1) return [palette.colours[Math.floor(palette.colours.length / 2)] ?? '#64748b'];

  const colours = Array.from({ length: count }, (_, index) => {
    const position = (index / (count - 1)) * (palette.colours.length - 1);
    const lowerIndex = Math.floor(position);
    const upperIndex = Math.min(palette.colours.length - 1, Math.ceil(position));
    const lower = palette.colours[lowerIndex] ?? '#ffffff';
    const upper = palette.colours[upperIndex] ?? lower;
    return interpolateHexColours(lower, upper, position - lowerIndex);
  });
  return reversed ? colours.reverse() : colours;
}
